import React from 'react'
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  ExternalLink,
  Clock,
  Hash,
  Layers,
  Maximize,
} from 'lucide-react'
import styles from './VisitorInfoHeader.module.scss'
import type { VisitorJourney } from '@/types/analytics'
import { countryWithFlag } from '@/utils/countryFlags'

interface VisitorInfoHeaderProps {
  journey: VisitorJourney
  className?: never
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function deviceIcon(deviceType: string): React.ReactNode {
  switch (deviceType.toLowerCase()) {
    case 'mobile':
      return <Smartphone />
    case 'tablet':
      return <Tablet />
    default:
      return <Monitor />
  }
}

export const VisitorInfoHeader: React.FC<VisitorInfoHeaderProps> = ({ journey }) => (
  <div>
    <div className={styles.header}>
      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Visitor Hash</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Hash /></span>
          <span className={styles.hashValue}>{journey.visitor_hash.slice(0, 12)}</span>
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>First Seen</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Clock /></span>
          {formatTimestamp(journey.first_seen_at)}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Last Seen</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Clock /></span>
          {formatTimestamp(journey.last_seen_at)}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Pages Viewed</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Layers /></span>
          {journey.pages_viewed}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Device</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}>{deviceIcon(journey.device_type)}</span>
          {journey.device_type}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Browser</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Globe /></span>
          {journey.browser}{journey.browser_version ? ` ${journey.browser_version}` : ''}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>OS</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Monitor /></span>
          {journey.os}{journey.os_version ? ` ${journey.os_version}` : ''}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Source</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><ExternalLink /></span>
          {journey.referrer_source}
        </span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>Country</span>
        <span className={styles.infoValue}>
          <span className={styles.infoIcon}><Globe /></span>
          {countryWithFlag(journey.country)}
        </span>
      </div>

      {journey.screen_width && (
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Screen Width</span>
          <span className={styles.infoValue}>
            <span className={styles.infoIcon}><Maximize /></span>
            {journey.screen_width}px
          </span>
        </div>
      )}
    </div>
    <div className={styles.note}>
      Visitor hashes rotate daily — this journey represents activity within a single day.
    </div>
  </div>
)
